import React, { useState } from "react";
import { Modal, Box, Typography } from "@mui/material";
import Button from "../../../../components/Button";
import InputField from "../../../../components/InputField";
import api from "../../../../api/api";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";

const ChangePasswordModal = ({ open, handleClose, user }) => {
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({});

  const [successOpen, setSuccessOpen] = useState(false);

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    const updatedData = {
      ...passwordData,
      [name]: value,
    };

    setPasswordData(updatedData);

    const errors = {};

    if (!updatedData.currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
    }

    if (!updatedData.newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (updatedData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    } else if (updatedData.newPassword === updatedData.currentPassword) {
      errors.newPassword =
        "New password cannot be the same as current password";
    }

    if (!updatedData.confirmPassword.trim()) {
      errors.confirmPassword = "Confirm password is required";
    } else if (updatedData.confirmPassword !== updatedData.newPassword) {
      errors.confirmPassword = "New password and confirm password must match";
    }

    setPasswordErrors(errors);
  };

  const validatePassword = () => {
    const errors = {};

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    } else if (passwordData.newPassword === passwordData.currentPassword) {
      errors.newPassword =
        "New password cannot be the same as current password";
    }

    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = "Confirm password is required";
    } else if (passwordData.confirmPassword !== passwordData.newPassword) {
      errors.confirmPassword = "New password and confirm password must match";
    }

    setPasswordErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleUpdatePassword = async () => {
    if (!validatePassword()) return;

    try {
      const token = localStorage.getItem("token");

      await api.post(
        "/change-password",
        {
          user_id: user?.user_id,
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setPasswordErrors({});

      handleClose();

      setSuccessOpen(true);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to change password");
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
  };

  const errorStyle = {
    marginTop: "-12px",
  };

  const iconStyle = {
    position: "absolute",
    right: "2px",
    top: "22px",
    zIndex: 2,
  };

  return (
    <>
      {/* Change Password Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            width: 450,
            bgcolor: "#fff",
            borderRadius: 2,
            p: 4,
            position: "absolute",
            top: "43%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
          }}
        >
          <h5 className="fw-bold mb-4">Change Password</h5>

          {/* Current Password */}
          <div className="mb-3">
            <div style={{ position: "relative" }}>
              <InputField
                label="Current Password"
                type={showPassword.currentPassword ? "text" : "password"}
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
              />

              <IconButton
                onClick={() => togglePassword("currentPassword")}
                style={iconStyle}
              >
                {showPassword.currentPassword ? (
                  <VisibilityOff />
                ) : (
                  <Visibility />
                )}
              </IconButton>
            </div>

            <div style={errorStyle}>
              {passwordErrors.currentPassword && (
                <small
                  style={{
                    fontSize: "0.725rem",
                    fontWeight: 600,
                    color: "#dc3545",
                  }}
                >
                  {passwordErrors.currentPassword}
                </small>
              )}
            </div>
          </div>

          {/* New Password */}
          <div className="mb-3">
            <div style={{ position: "relative" }}>
              <InputField
                label="New Password"
                type={showPassword.newPassword ? "text" : "password"}
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
              />

              <IconButton
                onClick={() => togglePassword("newPassword")}
                style={iconStyle}
              >
                {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>

            <div style={errorStyle}>
              {passwordErrors.newPassword && (
                <small
                  style={{
                    fontSize: "0.725rem",
                    fontWeight: 600,
                    color: "#dc3545",
                  }}
                >
                  {passwordErrors.newPassword}
                </small>
              )}
            </div>
          </div>

          {/* Confirm Password */}
          <div className="mb-4">
            <div style={{ position: "relative" }}>
              <InputField
                label="Confirm Password"
                type={showPassword.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
              />

              <IconButton
                onClick={() => togglePassword("confirmPassword")}
                style={iconStyle}
              >
                {showPassword.confirmPassword ? (
                  <VisibilityOff />
                ) : (
                  <Visibility />
                )}
              </IconButton>
            </div>

            <div style={errorStyle}>
              {passwordErrors.confirmPassword && (
                <small
                  style={{
                    fontSize: "0.725rem",
                    fontWeight: 600,
                    color: "#dc3545",
                  }}
                >
                  {passwordErrors.confirmPassword}
                </small>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>

            <Button variant="primary" onClick={handleUpdatePassword}>
              Update Password
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal open={successOpen} onClose={handleSuccessClose}>
        <Box
          sx={{
            width: 380,
            bgcolor: "#fff",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
          }}
        >
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Password Changed Successfully
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Your password has been updated successfully.
          </Typography>

          <Button variant="primary" onClick={handleSuccessClose}>
            OK
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default ChangePasswordModal;